/**
 * ADR-20261016-mew4 (refresh AC-10, AC-11): `ownerName` is nullable for a
 * renewal exposure-prepared before this feature shipped, with no
 * `SnapshotAccount.ContactNameEnc` value yet. Content Design Agent's own
 * fallback copy, shown in its place by screen 2's contributor state and
 * screen 7's two colleague states — never a blank, and never the
 * customer-facing literal "null" or "undefined".
 *
 * Re-authored 2026-10-19 (REQ-003): the fallback is a role, not an FRP
 * contact. The person who delegated the sections, and who will review,
 * sign and submit the renewal, is the renewal owner — a customer at the
 * business, never FRP's account manager. "Account Manager" is reserved
 * elsewhere in this codebase for FRP staff (see
 * `features/received/content.ts`'s `ownerSubmitted` copy); reusing it
 * here made a false statement about who signs and submits.
 *
 * Two forms, both resolving to the same underlying phrase, so a sentence
 * that opens with the owner's name keeps a capital letter whether or not
 * a real name is known: `resolveOwnerName` for every other position in a
 * sentence, `resolveOwnerNameCapitalized` only where the name is the
 * first word.
 */
export function resolveOwnerName(ownerName: string | null): string {
  return ownerName ?? "the renewal owner";
}

export function resolveOwnerNameCapitalized(ownerName: string | null): string {
  return ownerName ?? "The renewal owner";
}

/** H2 screen 7: the owner's first name, where the copy names them a second time
 * ("Dana will review..."). The first word of the name as held; the same fallback when none. */
export function resolveOwnerFirstName(ownerName: string | null): string {
  const first = ownerName?.trim().split(/\s+/)[0];
  return first !== undefined && first !== "" ? first : resolveOwnerName(null);
}

export function resolveOwnerFirstNameCapitalized(ownerName: string | null): string {
  const first = ownerName?.trim().split(/\s+/)[0];
  return first !== undefined && first !== "" ? first : resolveOwnerNameCapitalized(null);
}
